import React, { useEffect, useState } from "react";
import styles from "./sessionCardStyles";
import {
  bookSession,
  cancelSession,
  getExistingZoomLink,
} from "./timeTableService";
import { extractLocalTime } from "../../../utils/dateTimeUtils";
import { sessionActions } from "./sessionActions";
import { FaRegCopy, FaCheck } from "react-icons/fa";


const SessionCard = ({ session, refreshSessionStatus }) => {
  const [loadingAction, setLoadingAction] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [openAgenda, setOpenAgenda] = useState(false);
  const [minutesToStart, setMinutesToStart] = useState(null);
  const [minutesToEnd, setMinutesToEnd] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const getPlanTypeLabel = (planType) => {
  switch (planType) {
    case "GROUP":
      return "Talk with a dedicated peer";
    case "GROUP_DISCUSSION":
      return "Group discussion / debate";
    case "ONE_ON_ONE":
      return "1-on-1 personal practice session";
    default:
      return planType;
  }
};

const [copiedSectionId, setCopiedSectionId] = useState(null);

const handleCopySection = async (section) => {
  try {
    const textToCopy = section.questions
      ?.map((q, index) => `${index + 1}. ${q.questionText}`)
      .join("\n");

    await navigator.clipboard.writeText(textToCopy);

    // trigger visual feedback
    setCopiedSectionId(section.id);

    // reset after animation
    setTimeout(() => {
      setCopiedSectionId(null);
    }, 1500);

  } catch (err) {
    console.error("Copy failed", err);
  }
};




  /* ===============================
     RESPONSIVE DETECTION
  =============================== */
  const isMobile = window.innerWidth <= 768;

  /* ===============================
     TIME CALCULATION
  =============================== */
  useEffect(() => {
    if (!session?.startTime || !session?.endTime) return;

    const calculate = () => {
      const now = Date.now();
      setMinutesToStart(
        Math.floor(
          (new Date(session.startTime).getTime() - now) / (1000 * 60)
        )
      );
      setMinutesToEnd(
        Math.floor(
          (new Date(session.endTime).getTime() - now) / (1000 * 60)
        )
      );
    };

    calculate();
    const interval = setInterval(calculate, 60 * 1000);
    return () => clearInterval(interval);
  }, [session?.startTime, session?.endTime]);

  /* ===============================
     SESSION STATE
  =============================== */
  const isCancelled = session?.cancelled === true;

  const isBooked =
    session.buttonAction === sessionActions.CANCEL ||
    session.buttonAction === sessionActions.JOIN;

  const isExpired = minutesToEnd !== null && minutesToEnd < 0;

  const isPreparing =
    isBooked &&
    minutesToStart !== null &&
    minutesToStart <= 10 &&
    minutesToStart > 6;

  const isJoinWindow =
    isBooked &&
    ((minutesToStart <= 6 && minutesToEnd >= 0) ||
      (minutesToStart < 0 && minutesToEnd >= 0));

  const isBookingClosed =
    !isBooked &&
    minutesToStart !== null &&
    minutesToStart <= 10 &&
    minutesToEnd >= 0;

  const isSessionFull =
    !isBooked &&
    session.planType !== "ONE_ON_ONE" &&
    (session.buttonAction === sessionActions.FULL ||
      (session.remainingSlots !== undefined && session.remainingSlots <= 0));

  /* ===============================
     LABEL
  =============================== */
  const getButtonLabel = () => {
    if (isCancelled) return "CANCELLED";
    if (isExpired) return "EXPIRED";
    if (isJoinWindow) return "JOIN";
    if (isPreparing) return `Preparing session… ${minutesToStart}m`;
    if (isSessionFull) return "SESSION FULL";
    if (isBookingClosed) return "BOOKING CLOSED";
    return session.buttonAction;
  };

  /* ===============================
     BUTTON STYLE
  =============================== */
  const getButtonStyle = () => {
    if (isCancelled || isExpired) return styles.expiredBtn;
    if (isJoinWindow) return styles.joinBtn;
    if (isPreparing) return styles.preparingBtn;
    if (isSessionFull) return styles.bookingClosedBtn;
    if (isBookingClosed) return styles.bookingClosedBtn;

    switch (session.buttonAction) {
      case sessionActions.BOOK:
        return styles.bookBtn;
      case sessionActions.CANCEL:
        return styles.cancelBtn;
      case sessionActions.UPGRADE:
        return styles.upgradeBtn;
      default:
        return styles.bookBtn;
    }
  };

  /* ===============================
     ACTION HANDLER
  =============================== */
  const handleButtonClick = async () => {
    if (
      loadingAction ||
      isCancelled ||
      isExpired ||
      isPreparing ||
      isBookingClosed ||
      isSessionFull
    )
      return;

    const token = localStorage.getItem("token");

    if (!token && session.buttonAction === sessionActions.BOOK) {
      window.location.href = "/register";
      return;
    }

    setLoadingAction(true);

    try {
      if (isJoinWindow) {
        const res = await getExistingZoomLink(session.id);
        if (res.data && res.data !== "Zoom link will be available soon") {
          window.open(res.data, "_blank");
        } else {
          setAlertMessage("Zoom link will be available soon");
          setShowAlert(true);
        }
      } else {
        switch (session.buttonAction) {
          case sessionActions.BOOK:
            await bookSession(session.id);
            break;
          case sessionActions.CANCEL:
            await cancelSession(session.id);
            break;
          case sessionActions.UPGRADE:
            window.location.href = "/plans";
            break;
          default:
            break;
        }
      }

      refreshSessionStatus?.();
    } finally {
      setLoadingAction(false);
    }
  };

  /* ===============================
     RENDER
  =============================== */
  return (
    <div
      style={{
        ...styles.card,
        ...(isMobile ? styles.mobile.card : {}),
        backgroundColor: hovered
          ? styles.cardHover.background
          : styles.card.background,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
        }}
      >
        {/* LEFT */}
        <div
          style={{
            flex: 1,
            position: isMobile ? "relative" : "static",
            paddingRight: isMobile ? 70 : 0,
          }}
        >
          {/* MOBILE TIME (TOP-RIGHT) */}
         {isMobile && (
  <div
    style={{
      position: "absolute",
      top: 0,
      right: 0,
      fontSize: styles.mobile.timeText.fontSize,
      fontWeight: 700,
      color: "#555",            // subtle, readable
      whiteSpace: "nowrap",
    }}
  >
    ⏰ {extractLocalTime(session.startTime)}
  </div>
)}

          {session.speakerName && (
            <div
              style={{
                fontSize: isMobile
                  ? styles.mobile.subtitle.fontSize
                  : 16,
                fontWeight: 600,
              }}
            >
              🎤 {session.speakerName}
            </div>
          )}

          <div
            style={{
              fontSize: isMobile
                ? styles.mobile.title.fontSize
                : 20,
              fontWeight: "bold",
            }}
          >
            {session.topic}
          </div>

          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {getPlanTypeLabel(session.planType)}
          </div>

          {isCancelled ? (
  <div
    style={{
      ...styles.cancelledBadge,
      ...(isMobile
        ? {
            ...styles.mobile.badge,
            fontSize: 12, // ⬆️ mobile only
          }
        : {}),
    }}
  >
    ❌ Session Cancelled by Host
  </div>
) : isBooked ? (
  <div
    style={{
      ...styles.bookedBadge,
      ...(isMobile
        ? {
            ...styles.mobile.badge,
            fontSize: 12, // ⬆️ mobile only
          }
        : {}),
    }}
  >
    ✅ Session Booked. See you in the meeting!
  </div>
) : null}

        </div>

        {/* RIGHT (DESKTOP ONLY TIME) */}
        <div
          style={{
            textAlign: isMobile ? "left" : "right",
            minWidth: isMobile ? "100%" : 160,
          }}
        >
          {!isMobile && (
            <div style={{ fontSize: 26, fontWeight: "bold" }}>
              ⏰ {extractLocalTime(session.startTime)}
            </div>
          )}

   {session.remainingSlots !== undefined &&
  session.remainingSlots <= 3 &&
  session.remainingSlots > 0 &&
  !isExpired &&
  session.planType !== "ONE_ON_ONE" && (
    <div style={{ fontWeight: 600, color: "#b91c1c" }}>
      ⚠️ Only {session.remainingSlots} slot
      {session.remainingSlots === 1 ? "" : "s"} left
    </div>
)}


{session.buttonAction === sessionActions.UPGRADE &&
  session.upgradeReason && (
    <div
      style={{
        marginTop: 6,
        marginBottom: 6,

        // ✅ Mobile stays same
        fontSize: isMobile ? 12 : 14,

        color: "#b45309",
        fontWeight: 500,
        lineHeight: 1.4,
      }}
    >
      ℹ️ {session.upgradeReason}
    </div>
  )}



            
            <button
  style={{
    ...getButtonStyle(),
    ...(isMobile ? styles.mobile.button : {}),
    marginTop: 10,

    // ✅ Mobile: unchanged
    ...(isMobile
      ? { width: "100%" }
      : {
          // ✅ Desktop only
          width: 180,
          minWidth: 180,
        }),
  }}
  onClick={handleButtonClick}
  disabled={
    loadingAction ||
    isCancelled ||
    isExpired ||
    isPreparing ||
    isBookingClosed ||
    isSessionFull
  }
>
  <span
    style={{
      ...(isPreparing ? styles.preparingText : {}),
      // ✅ Prevent text from breaking button width on desktop
      whiteSpace: "nowrap",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    }}
  >
    {loadingAction ? "Please wait..." : getButtonLabel()}
  </span>
</button>

        </div>
      </div>

      {/* ===== AGENDA ===== */}
{session.agendas?.length > 0 && (
  <>
    <button
      style={styles.dropdownToggle}
      onClick={() => setOpenAgenda(!openAgenda)}
    >
      {openAgenda ? "Hide discussion ▲" : "View discussion ▼"}
    </button>

    {openAgenda && (
      <div
        style={{
          ...styles.agendaGrid,
          ...(isMobile ? styles.mobile.agendaGrid : {}),
        }}
      >
        {session.agendas.map((agenda) => (
          <div key={agenda.id} style={styles.agendaCard}>
            <div
              style={{
                ...styles.agendaTitle,
                ...(isMobile ? styles.mobile.agendaTitle : {}),
              }}
            >
              📌 {agenda.title}
            </div>

            {/* SECTIONS */}
            <div
              style={{
                display: isMobile ? "block" : "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              {agenda.sections?.map((section) => (
                <div key={section.id}>
                 <div
  style={{
    ...styles.sectionTitle,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 6,

    // dim background when copied
    backgroundColor:
      copiedSectionId === section.id
        ? "rgba(0,0,0,0.06)"
        : "transparent",

    transition: "all 0.25s ease",
  }}
>
  {/* Copy icon LEFT */}
  <span
    onClick={() => handleCopySection(section)}
    style={{
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      color:
        copiedSectionId === section.id
          ? "#16a34a"
          : "#6b7280",
      transition: "all 0.2s ease",
    }}
  >
    {copiedSectionId === section.id ? (
      <FaCheck size={14} />
    ) : (
      <FaRegCopy size={14} />
    )}
  </span>

  {/* Section name */}
  <span>
    {section.section}
  </span>
</div>


                  {section.questions?.map((q) => (
                    <div
                      key={q.id}
                      style={{
                        ...styles.question,
                        ...(isMobile ? styles.mobile.question : {}),
                      }}
                    >
                      • {q.questionText}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </>
)}

      {/* ===== ALERT ===== */}
      {showAlert && (
        <div style={styles.customAlertOverlay}>
          <div style={styles.customAlertBox}>
            <div style={styles.customAlertText}>{alertMessage}</div>
            <button
              style={styles.customAlertButton}
              onClick={() => setShowAlert(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionCard;
